// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/ordemServicoService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type OsListaResumo } from './listaFiltros';
import type {
  OsResumo,
  OsDetalhe,
  CriarOsRequest,
  AtualizarOsRequest,
  OsEquipamento,
  OsEquipamentoRequest,
  OsTecnico,
  OsTecnicoRequest,
  OsAcompanhamento,
  OsAcompanhamentoRequest,
  OsOcorrencia,
  OsOcorrenciaRequest,
  OsTarefa,
  OsTarefaRequest,
  OsDespesa,
  OsDespesaRequest,
} from '../types';

const BASE = '/api/os';

export const ordemServicoService = {
  async getAll(pagina = 1, tamanhoPagina = 20, filtros: ListaFiltros = {}): Promise<PaginadoComResumo<OsResumo, OsListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<OsResumo, OsListaResumo>>(BASE, {
      params: paramsDaLista(pagina, tamanhoPagina, filtros),
    });
    return data;
  },

  async getAbertas(): Promise<OsResumo[]> {
    const { data } = await apiClient.get<OsResumo[]>(`${BASE}/abertas`);
    return data;
  },

  /** PDF A4 da OS (com fotos das tarefas e despesas). */
  async gerarPdf(id: number): Promise<Blob> {
    const { data } = await apiClient.get(`${BASE}/${id}/pdf`, { responseType: 'blob' });
    return data as Blob;
  },

  async getPorPeriodo(inicio: string, fim: string): Promise<OsResumo[]> {
    const { data } = await apiClient.get<OsResumo[]>(`${BASE}/periodo`, {
      params: { inicio, fim },
    });
    return data;
  },

  async getById(id: number): Promise<OsDetalhe> {
    const { data } = await apiClient.get<OsDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarOsRequest): Promise<OsDetalhe> {
    const { data } = await apiClient.post<OsDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarOsRequest): Promise<OsResumo> {
    const { data } = await apiClient.put<OsResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  // ── Equipamentos (replace-all) ──────────────────────────────────────────
  async getEquipamentos(id: number): Promise<OsEquipamento[]> {
    const { data } = await apiClient.get<OsEquipamento[]>(`${BASE}/${id}/equipamentos`);
    return data;
  },

  async saveEquipamentos(id: number, itens: OsEquipamentoRequest[]): Promise<OsEquipamento[]> {
    const { data } = await apiClient.put<OsEquipamento[]>(`${BASE}/${id}/equipamentos`, itens);
    return data;
  },

  // ── Técnicos executores (replace-all) ───────────────────────────────────
  async getTecnicos(id: number): Promise<OsTecnico[]> {
    const { data } = await apiClient.get<OsTecnico[]>(`${BASE}/${id}/tecnicos`);
    return data;
  },

  async saveTecnicos(id: number, itens: OsTecnicoRequest[]): Promise<OsTecnico[]> {
    const { data } = await apiClient.put<OsTecnico[]>(`${BASE}/${id}/tecnicos`, itens);
    return data;
  },

  // ── Acompanhamento / Status (append-only) ───────────────────────────────
  async getAcompanhamentos(id: number): Promise<OsAcompanhamento[]> {
    const { data } = await apiClient.get<OsAcompanhamento[]>(`${BASE}/${id}/acompanhamentos`);
    return data;
  },

  async addAcompanhamento(id: number, req: OsAcompanhamentoRequest): Promise<OsAcompanhamento> {
    const { data } = await apiClient.post<OsAcompanhamento>(`${BASE}/${id}/acompanhamentos`, req);
    return data;
  },

  // ── Ocorrências (append-only) ───────────────────────────────────────────
  async getOcorrencias(id: number): Promise<OsOcorrencia[]> {
    const { data } = await apiClient.get<OsOcorrencia[]>(`${BASE}/${id}/ocorrencias`);
    return data;
  },

  async addOcorrencia(id: number, req: OsOcorrenciaRequest): Promise<OsOcorrencia> {
    const { data } = await apiClient.post<OsOcorrencia>(`${BASE}/${id}/ocorrencias`, req);
    return data;
  },

  async deleteOcorrencia(id: number, ocorrenciaId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}/ocorrencias/${ocorrenciaId}`);
  },

  // ── Tarefas / checklist (replace-all) ───────────────────────────────────
  async getTarefas(id: number): Promise<OsTarefa[]> {
    const { data } = await apiClient.get<OsTarefa[]>(`${BASE}/${id}/tarefas`);
    return data;
  },

  async saveTarefas(id: number, itens: OsTarefaRequest[]): Promise<OsTarefa[]> {
    const { data } = await apiClient.put<OsTarefa[]>(`${BASE}/${id}/tarefas`, itens);
    return data;
  },

  // ── Despesas (replace-all) ──────────────────────────────────────────────
  async getDespesas(id: number): Promise<OsDespesa[]> {
    const { data } = await apiClient.get<OsDespesa[]>(`${BASE}/${id}/despesas`);
    return data;
  },

  async saveDespesas(id: number, itens: OsDespesaRequest[]): Promise<OsDespesa[]> {
    const { data } = await apiClient.put<OsDespesa[]>(`${BASE}/${id}/despesas`, itens);
    return data;
  },

  // ── Imagens de tarefas (slot = 'inicio' | 'fim') e despesas ──────────────
  async uploadTarefaImagem(id: number, tarefaId: number, slot: 'inicio' | 'fim', file: File): Promise<void> {
    const fd = new FormData();
    fd.append('arquivo', file);
    await apiClient.post(`${BASE}/${id}/tarefas/${tarefaId}/imagem/${slot}`, fd);
  },

  async deleteTarefaImagem(id: number, tarefaId: number, slot: 'inicio' | 'fim'): Promise<void> {
    await apiClient.delete(`${BASE}/${id}/tarefas/${tarefaId}/imagem/${slot}`);
  },

  async uploadDespesaImagem(id: number, despesaId: number, file: File): Promise<void> {
    const fd = new FormData();
    fd.append('arquivo', file);
    await apiClient.post(`${BASE}/${id}/despesas/${despesaId}/imagem`, fd);
  },

  async deleteDespesaImagem(id: number, despesaId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}/despesas/${despesaId}/imagem`);
  },
};
